// === FEATURE: AngelicBumperView ===
// The Seraphim Bumper used to live cramped inside the PlayerDock. It never fit.
// Now it gets its OWN full-screen, cinematic IMMERSIVE VIEW — a unique,
// individual experience that LAUNCHES on demand (the dock just has a launcher
// button now). It's a deep-underground, CRT/VHS-toned backdrop with the
// MusicBumper visualizer mounted large and centered as the sole centerpiece.
//
// MINIMIZE: the immersive view can shrink into a persistent MINI player that
// stays pinned to the screen while the visitor scrolls the rest of the page —
// so the music keeps going while they explore. This is purely an INTERNAL
// state of this component (App still owns open/close); minimizing NEVER
// unmounts the <MusicBumper/>. The exact same MusicBumper instance is kept
// mounted the whole time — full-screen vs mini is only a className swap on its
// container, so the AudioContext, the rAF loop, the <audio> element AND the
// shared-audioBus claim all persist uninterrupted. Toggling minimized does NOT
// stop playback.
//
// Contract with App:
//   props.open    — boolean; when false this renders NOTHING (so MusicBumper is
//                   fully unmounted → its AudioContext/rAF tear down and it
//                   releases the shared audioBus, per its own lifecycle).
//   props.onClose — called by the X / "back to the zone" affordance AND by the
//                   Escape key (from the full-screen view).
//   props.sound   — global sound toggle, passed straight through to MusicBumper.
//
// Audio coordination is unchanged: MusicBumper still claims the shared audioBus
// on play, so launching + playing it here pauses StaticFM / the deck.
//
// z-index: the full-screen overlay sits at 40000 and the mini-player at 39000 —
// both ABOVE the PlayerDock (9000) + page content, but BELOW the SystemCrash +
// X-ray overlays (100000) so a crash/x-ray still wins.

import * as React from "react";
import { useEffect, useRef, useCallback, useState } from "react";
import MusicBumper from "../MusicBumper/MusicBumper";
import "./angelicbumperview.css";

function AngelicBumperView({ open = false, onClose, sound = true }) {
  const closeBtnRef = useRef(null);
  // Imperative handle to the MusicBumper instance so the mini player's own
  // play/pause button can drive playback even when the bumper's built-in
  // transport is scrolled out of the compact (clipped) mini frame.
  const bumperRef = useRef(null);

  // INTERNAL state: is the view shrunk into the persistent mini player? This is
  // owned entirely here — App only knows about open/close. We deliberately keep
  // this state alive across the open prop so the SAME component instance (and
  // therefore the SAME mounted MusicBumper) survives a minimize/expand toggle.
  const [minimized, setMinimized] = useState(false);

  const handleClose = useCallback(() => {
    // Reset to full-screen for next launch, then ask App to close (unmount).
    setMinimized(false);
    if (typeof onClose === "function") onClose();
  }, [onClose]);

  const minimize = useCallback(() => setMinimized(true), []);
  const expand = useCallback(() => setMinimized(false), []);

  // Escape behaviour: from the full-screen view, Escape minimizes into the mini
  // player (keep enjoying the music) rather than killing it. From the mini
  // player there's no global Escape grab so the rest of the page keeps Escape.
  // (Close is always available via the X.)
  useEffect(() => {
    if (!open || minimized) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape" || e.key === "Esc") {
        e.stopPropagation();
        handleClose();
      }
    };
    window.addEventListener("keydown", onKey);

    // Move focus to the close affordance so Escape/Enter are reachable and the
    // entrance reads as a deliberate "view" the visitor stepped into.
    const raf = window.requestAnimationFrame(() => {
      if (closeBtnRef.current) {
        try {
          closeBtnRef.current.focus({ preventScroll: true });
        } catch (err) {
          closeBtnRef.current.focus();
        }
      }
    });

    return () => {
      window.removeEventListener("keydown", onKey);
      window.cancelAnimationFrame(raf);
    };
  }, [open, minimized, handleClose]);

  // Render NOTHING when closed — this fully unmounts MusicBumper.
  if (!open) return null;

  // The MusicBumper is declared ONCE and rendered inside ONE persistent tree
  // below, so it is NEVER unmounted/remounted when we flip between the
  // full-screen overlay and the mini player. (It previously lived in two
  // separate `return`s — React saw the bumper under a different parent each time
  // and tore it down + rebuilt it, recreating <audio id="drbumper"> and
  // SILENCING playback on minimize.) Now the root element persists (only its
  // class swaps), the chrome is a keyed either/or branch, and the bumper's host
  // is a stable keyed child slot — so the Visualizer, its <audio>, AudioContext,
  // analyser and the shared audioBus claim all survive the toggle and the music
  // keeps playing.
  //
  // `compact` is true ONLY in the mini player: it renders the tight mini layout
  // (one marquee, a slim visualizer strip, a transport row that fits).
  const stage = (
    <div className="abv-stage">
      <MusicBumper ref={bumperRef} sound={sound} compact={minimized} />
    </div>
  );

  // ---- MINI CHROME ----------------------------------------------------------
  // The pinned mini player's frame: title + EXPAND + CLOSE. No play button here
  // — the single play/pause lives in the bumper's own transport row.
  const miniChrome = (
    <React.Fragment key="abv-chrome-mini">
      <div className="abv-mini-scanlines" aria-hidden="true" />
      <div className="abv-mini-bar">
        <span className="abv-mini-title" title="Seraphim Bumper">
          SERAPHIM BUMPER
        </span>
        <div className="abv-mini-chrome">
          <button
            type="button"
            className="abv-mini-btn"
            onClick={expand}
            aria-label="Expand the Seraphim Bumper to full screen"
            title="Expand to full screen"
          >
            &#9633;
          </button>
          <button
            type="button"
            className="abv-mini-btn abv-mini-x"
            onClick={handleClose}
            aria-label="Close the Seraphim Bumper"
            title="Close"
          >
            &times;
          </button>
        </div>
      </div>
    </React.Fragment>
  );

  // ---- UNIFIED RENDER — one persistent root; the chrome swaps, the bumper stays.
  return (
    <div
      className={minimized ? "abv-mini" : "abv-overlay"}
      role={minimized ? "region" : "dialog"}
      aria-modal={minimized ? undefined : "true"}
      aria-label={minimized ? "Seraphim Bumper mini player" : "The Seraphim Bumper"}
    >
      {minimized ? miniChrome : (
        <React.Fragment key="abv-chrome-full">
      {/* ---- SACRED LIGHT LAYERS ----
          Purely visual, wordless reverence wrapped around the centerpiece so the
          whole view feels transcendent and worthy of praise. Every layer is
          decorative (aria-hidden), animated only with transform/opacity, and
          calmed under prefers-reduced-motion. NO text lives here — the light
          itself is the praise. */}

      {/* A heavenly gradient wash that slowly breathes behind everything. */}
      <div className="abv-heaven" aria-hidden="true" />

      {/* Ascending shafts of light / godrays climbing from on high. */}
      <div className="abv-godrays" aria-hidden="true" />

      {/* A soft gold aureole / halo bloom radiating from the centerpiece, with a
          slow radiant breathing pulse. */}
      <div className="abv-aureole" aria-hidden="true" />

      {/* Concentric rings of soft seraphim light, turning ever so slowly. */}
      <div className="abv-seraph-rings" aria-hidden="true">
        <span className="abv-ring abv-ring--1" />
        <span className="abv-ring abv-ring--2" />
        <span className="abv-ring abv-ring--3" />
      </div>

      {/* Gentle drifting motes — slow embers of holy light rising upward. */}
      <div className="abv-motes" aria-hidden="true">
        <span style={{ left: "12%", animationDelay: "0s", animationDuration: "15s" }} />
        <span style={{ left: "23%", animationDelay: "4s", animationDuration: "19s" }} />
        <span style={{ left: "34%", animationDelay: "8s", animationDuration: "13s" }} />
        <span style={{ left: "45%", animationDelay: "2s", animationDuration: "21s" }} />
        <span style={{ left: "57%", animationDelay: "10s", animationDuration: "16s" }} />
        <span style={{ left: "66%", animationDelay: "6s", animationDuration: "18s" }} />
        <span style={{ left: "77%", animationDelay: "12s", animationDuration: "14s" }} />
        <span style={{ left: "86%", animationDelay: "3s", animationDuration: "20s" }} />
        <span style={{ left: "94%", animationDelay: "9s", animationDuration: "17s" }} />
      </div>

      {/* Cinematic backdrop layers: deep-underground gradient + CRT scanlines.
          All purely decorative. */}
      <div className="abv-scanlines" aria-hidden="true" />
      <div className="abv-vignette" aria-hidden="true" />

      {/* Close / back affordance — wired to onClose (Escape also closes). */}
      <button
        type="button"
        ref={closeBtnRef}
        className="abv-back"
        onClick={handleClose}
        aria-label="Back to the zone"
        title="Back to the zone (Esc)"
      >
        &larr; back to the zone
      </button>

      <div className="abv-corner-controls">
        {/* MINIMIZE — shrink into the persistent mini player; music keeps
            playing while the visitor scrolls the rest of the page. */}
        <button
          type="button"
          className="abv-mini-toggle"
          onClick={minimize}
          aria-label="Minimize to mini player and keep the music playing"
          title="Minimize (keep playing)"
        >
          &#8211;
        </button>
        <button
          type="button"
          className="abv-close"
          onClick={handleClose}
          aria-label="Close the Seraphim Bumper"
          title="Close (Esc)"
        >
          &times;
        </button>
      </div>

        </React.Fragment>
      )}

      {/* The centerpiece — the SAME MusicBumper in BOTH layouts. A keyed host at a
          stable child slot means React never remounts it, so the <audio> (and the
          music) survives minimize. `display:contents` in full mode makes this host
          vanish so the stage centers exactly as before; `.abv--mini` shrinks it
          into the pinned player when minimized. */}
      <div
        key="abv-stage-host"
        className={"abv-stage-host" + (minimized ? " abv--mini" : "")}
      >
        {stage}
      </div>
    </div>
  );
}

export default AngelicBumperView;
